import React, { useState } from 'react';
import { useSpring } from '@react-spring/web';
import NextImage from 'next/image';
import { useTheme } from 'styled-components';

import { CourseCardProps } from '..';

import Pill from 'Elements/Pill/Pill';

import { ImageWrapper, NameLink, DegreeType, CategoriesList, PillItem } from '../Course.styled';

import {
    StyledContainer,
    StyledImageContainer,
    StyledImageLink,
    StyledInner,
    StyledName
} from './Mini.styled';

export interface Props
    extends Omit<
        CourseCardProps,
        | 'variants'
        | 'disabledCompareChecked'
        | 'defaultCompareChecked'
        | 'addCourseToCompare'
        | 'removeCourseToCompare'
        | 'compareCourseData'
    > {
    courseOptions?: string[];
}

const Mini: React.FC<Props> = props => {
    const { degreeType, courseName, link, studyLevel, categories, image, status } = props;

    const { colors } = useTheme();
    const [isHovering, setIsHovering] = useState(false);

    const imageSpring = useSpring({
        transform: isHovering ? 'scale(1.03)' : 'scale(1)'
    });

    return (
        <StyledContainer className="c-card c-course-card">
            <StyledImageContainer>
                <StyledImageLink
                    href={link}
                    aria-label={courseName}
                    data-hover={isHovering}
                    onMouseOver={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                >
                    <ImageWrapper style={imageSpring}>
                        <NextImage {...image} alt="" fill style={{ objectFit: 'cover' }} />
                    </ImageWrapper>
                </StyledImageLink>
            </StyledImageContainer>
            <StyledInner>
                <NameLink
                    href={link}
                    aria-label={courseName}
                    data-hover={isHovering}
                    onMouseOver={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                >
                    <DegreeType>{degreeType}</DegreeType>
                    <StyledName>{courseName}</StyledName>
                </NameLink>
                <CategoriesList>
                    {studyLevel && (
                        <PillItem>
                            <Pill
                                textColor={
                                    studyLevel === 'Postgraduate'
                                        ? colors.accents[9].hex
                                        : colors.tertiary.hex
                                }
                                backgroundColor={
                                    studyLevel === 'Postgraduate'
                                        ? `rgb(${colors.accents[9].rgb}, 0.1)`
                                        : `rgb(${colors.tertiary.rgb}, 0.1)`
                                }
                            >
                                {studyLevel}
                            </Pill>
                        </PillItem>
                    )}
                    {categories &&
                        categories.length > 0 &&
                        categories.map((category, index) => (
                            <PillItem key={index}>
                                <Pill
                                    textColor={colors.accents[5].hex}
                                    backgroundColor={`rgb(${colors.accents[5].rgb}, 0.1)`}
                                >
                                    {category}
                                </Pill>
                            </PillItem>
                        ))}
                    {(status === 'clearing' || status === 'closed') && (
                        <PillItem>
                            <Pill
                                textColor={colors.primary.hex}
                                backgroundColor={`rgb(${colors.primary.rgb}, 0.1)`}
                            >
                                {status === 'clearing' ? 'Clearing open' : 'Closed'}
                            </Pill>
                        </PillItem>
                    )}
                </CategoriesList>
            </StyledInner>
        </StyledContainer>
    );
};

export default Mini;
